import classnames from 'classnames';
import styles from './index.module.less';
import type {
  Tools,
  Theme,
  SupportedHtmlTags,
  BenefitItem,
} from '../../../core/types';
import { Color, Align } from '../../../core/enums';
import withEventTracking from '../../../utilities/withEventTracking';
import { Icon } from '../../atoms/icon';
import { AccordionList } from '../../styleless/accordion-list';
import { useBreakpoint } from '../../styleless/breakpoint';
import Element from '../../../core/Element';
import { Fragment } from 'react';

interface ButtonProps {
  onClick: (event: React.SyntheticEvent) => void;
  children: React.ReactNode;
  active: boolean;
  tools: Tools;
  itemId: string;
  headerId: string;
  buttonWrapper?: keyof HTMLElementTagNameMap;
}

interface WrapperProps {
  Component?: keyof HTMLElementTagNameMap;
  children: React.ReactNode;
}

const Wrapper = ({ Component, children }: WrapperProps) => {
  const WrapperComponent = Component ?? Fragment;
  return <WrapperComponent>{children}</WrapperComponent>;
};

const Button = withEventTracking<ButtonProps>(
  ({
    onClick,
    active,
    children,
    itemId,
    headerId,
    buttonWrapper,
  }: ButtonProps) => {
    return (
      <Wrapper Component={buttonWrapper}>
        <Element
          Component="button"
          type="button"
          className={classnames(styles.button, {
            [styles.active]: active,
          })}
          aria-controls={itemId}
          aria-expanded={active}
          aria-labelledby={headerId}
          onClick={onClick}
          theme={{
            paddingTop: 25,
            paddingBottom: 25,
            paddingLeft: 15,
            paddingRight: 15,
          }}
        >
          {children}
        </Element>
      </Wrapper>
    );
  }
);

export interface AccordionCustomization {
  $layout?: 'standard' | 'compact';
  label?: {
    paddingLeft?: number;
    color?: Color;
  };
  labelText?: Theme;
  descriptionText?: Theme;
  labelHtmlTag?: keyof SupportedHtmlTags;
  descriptionHtmlTag?: keyof SupportedHtmlTags;
  titleHtmlTag?: keyof SupportedHtmlTags;
  textHtmlTag?: keyof SupportedHtmlTags;
  bodyTitleTheme?: Theme;
  bodyWrapperTheme?: Theme;
  imageTheme?: Theme;
}

export interface Props {
  data: {
    items: BenefitItem[];
  };
  qeId?: string;
  $openMultiple?: boolean;
  $expandItemByIndex?: number;
  $expandAllItems?: boolean;
  $verticallyCenterIcon?: boolean;
  customization?: AccordionCustomization;
  itemTag?: keyof SupportedHtmlTags;
  onSelectItem?: (event: Event, item: BenefitItem, isActive: boolean) => void;
  buttonWrapper?: keyof SupportedHtmlTags;
}

const defaultCustomizations: AccordionCustomization = {
  $layout: 'standard',
  label: {},
  labelHtmlTag: 'span',
  descriptionHtmlTag: 'span',
  titleHtmlTag: 'p',
  textHtmlTag: 'div',
  bodyTitleTheme: {},
  bodyWrapperTheme: { padding: 25 },
  imageTheme: { horizontallyFluid: true },
};

export const AccordionStackedContent = ({
  data: { items },
  $openMultiple,
  $expandItemByIndex,
  $expandAllItems,
  $verticallyCenterIcon = true,
  customization,
  onSelectItem,
  qeId = 'AccordionStackedContent',
  itemTag,
  buttonWrapper,
}: Props): React.ReactElement => {
  const { current } = useBreakpoint();
  const getItemId = (index: number) => `${qeId}_${String(index)}`;
  const {
    $layout,
    label: labelStyle,
    labelText: labelTextStyle,
    labelHtmlTag,
    descriptionText: descriptionTextStyle,
    descriptionHtmlTag,
    bodyTitleTheme,
    bodyWrapperTheme,
    imageTheme,
    titleHtmlTag,
    textHtmlTag,
  } = {
    ...defaultCustomizations,
    ...customization,
  };

  const bodyItemStyle =
    $layout === 'compact'
      ? {
          title: { fontSize: '20px' },
          content: { paddingLeft: 30, paddingRight: 30 },
        }
      : {
          title: {},
          content: {},
        };

  return (
    <AccordionList
      Component={itemTag}
      $expandAllItems={$expandAllItems}
      $openMultiple={$openMultiple}
      listClassName={styles.root}
      itemClassName={styles.item}
      $expandItemByIndex={$expandItemByIndex}
      items={items.map((item, index) => {
        const {
          label,
          description,
          body = [],
          icon,
          image,
          trackingDetails,
          trackingElement,
        } = item;
        const headerId = `${getItemId(index)}_header`;
        return {
          Button: ({
            onClick,
            active,
          }: {
            onClick: (
              event: React.MouseEvent<HTMLButtonElement, MouseEvent> | Event
            ) => void;
            active: boolean;
          }) => (
            <Button
              onClick={(event: React.SyntheticEvent) => {
                event.persist();

                onClick(event.nativeEvent);

                if (onSelectItem) {
                  onSelectItem(event.nativeEvent, item, active);
                }
              }}
              active={active}
              itemId={getItemId(index)}
              headerId={headerId}
              tools={{
                tracking: {
                  methods: ['click'],
                  element: trackingElement
                    ? `${trackingElement}:AllBenefits`
                    : 'AllBenefits',
                  details: [
                    trackingDetails ? trackingDetails : label,
                    active ? 'Close' : 'Open',
                  ],
                },
              }}
              buttonWrapper={buttonWrapper}
            >
              <>
                {$verticallyCenterIcon && icon && (
                  <Icon {...icon} theme={{ color: Color.DeepBlue }} />
                )}
                <Element id={headerId} className={styles.buttonText}>
                  {!$verticallyCenterIcon && icon && (
                    <Icon {...icon} theme={{ color: Color.DeepBlue }} />
                  )}
                  <Element
                    className={styles.label}
                    theme={{
                      textAlign: Align.Left,
                      paddingLeft: 25,
                      paddingRight: 25,
                      ...labelStyle,
                    }}
                  >
                    <Element
                      Component={labelHtmlTag}
                      theme={{
                        textBody: 3,
                        ...labelTextStyle,
                      }}
                    >
                      {label}
                    </Element>
                    {description && (
                      <Element
                        Component={descriptionHtmlTag}
                        theme={{
                          textBody: 3,
                          ...descriptionTextStyle,
                        }}
                      >
                        {description}
                      </Element>
                    )}
                  </Element>
                </Element>
                <Icon
                  directory="us-consumer"
                  imageName={
                    active ? 'axp-icon-minuscircle' : 'axp-icon-pluscircle'
                  }
                  theme={{ color: Color.BrightBlue }}
                />
              </>
            </Button>
          ),
          Body: () => (
            <Element id={getItemId(index)}>
              {image && (
                <Element
                  Component="img"
                  src={
                    typeof image === 'object'
                      ? image[current] || image.default
                      : image
                  }
                  alt={label}
                  theme={{ ...imageTheme }}
                  className={styles.image}
                />
              )}
              <Element theme={{ ...bodyWrapperTheme }}>
                {body.map(({ title, text }, i) => (
                  <Element
                    key={`${i}-${title}`}
                    theme={{
                      paddingTop: 'none',
                      paddingBottom: 15,
                      ...bodyItemStyle.content,
                    }}
                  >
                    {title && (
                      <Element
                        Component={titleHtmlTag}
                        theme={{
                          textHeading: 2,
                          color: Color.DeepBlue,
                          marginBottom: 'none',
                          ...bodyTitleTheme,
                        }}
                        style={bodyItemStyle.title}
                      >
                        {title}
                      </Element>
                    )}{' '}
                    {text && (
                      <Element
                        Component={textHtmlTag}
                        theme={{ textBody: 3, marginTop: 10 }}
                      >
                        {text}
                      </Element>
                    )}
                  </Element>
                ))}
              </Element>
            </Element>
          ),
        };
      })}
      qeId={qeId}
    />
  );
};
